import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import CardWrapper from 'components/Card/CardWrapper';
import CardBody from 'components/Card/CardBody';
import { makeStyles } from '@material-ui/styles';
import { FormTextInput } from 'components';
import RegularCheckbox from 'components/Checkbox/RegularCheckbox';
import { Dialog, FormLabel, Switch } from '@material-ui/core';
import SelectInput from 'components/FormSelect/SelectInput';
import FormControlButtons from 'components/FormControlButtons/FormControlButtons';
import {
  cadetBlueGrayColor,
  dangerColor,
  grayColor,
  navyColor,
  successColor,
} from 'assets/jss/material-dashboard-pro-react';
import { useSelector } from 'react-redux';
import { get, put } from 'helpers/apiHelpers';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import InfoWithTooltip from 'components/InfoWithTooltip/InfoWithTooltip';
import InfoIconTooltip from 'components/InfoIconTooltip/InfoIconTooltip';
import SweetAlert from 'react-bootstrap-sweetalert';
import buttonStyle from 'assets/jss/material-dashboard-pro-react/components/buttonStyle';
import EXTERNAL_URLS from 'constants/externalUrls';
import moment from 'moment';

const useStyles = makeStyles(() => ({
  ...buttonStyle,
  cardBody: { padding: '16px', fontWeight: '400' },
  titleWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  titleParagraph: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
    fontWeight: '300',
    fontSize: '16px',
    color: grayColor[2],
  },
  checkboxWrapper: { display: 'flex', alignItems: 'center', gap: '8px' },
  ul: {
    margin: '0 0 40px',
    paddingLeft: '20px',
    lineHeight: '20px',
  },
  checkbox: { padding: '0' },
  switchWrapper: { margin: '48px 0 0' },
  formInputWrapper: {
    display: 'flex',
    gap: '24px',
    maxWidth: '892px',
    margin: '14px 0 10px',
  },
  bold: { fontWeight: '600' },
  statusTile: {
    marginLeft: '8px',
    padding: '4px 8px',
    backgroundColor: grayColor[10],
    borderRadius: '4px',
    fontWeight: '500',
  },
  statusEnabled: {
    backgroundColor: `${successColor[4]}20`,
    color: successColor[4],
  },
  statusDisabled: {
    backgroundColor: `${dangerColor[4]}20`,
    color: dangerColor[4],
  },
  switch: { marginLeft: '-10px' },

  customSwitchBase: {
    height: 24,
    color: grayColor[0],
    '& + $customBar': {
      backgroundColor: grayColor[5],
      opacity: 1,
    },
    '&$customChecked + $customBar': {
      backgroundColor: cadetBlueGrayColor,
      opacity: 1,
    },
  },
  customChecked: { color: navyColor },
  customBar: {
    borderRadius: 9999,
    opacity: 1,
  },
  zoneInfoUpdateWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  lastUpdateWrapper: { display: 'flex', alignItems: 'center' },
  lastUpdate: { color: grayColor[14], marginLeft: '16px' },
  lastUpdateDate: {
    color: grayColor[14],
    fontWeight: '500',
    marginLeft: '4px',
  },
  selectWrapper: { display: 'flex', flexDirection: 'column', width: '100%' },
  select: { padding: '6px 0 2px 0', color: grayColor[14], fontSize: '12px' },

  label: { paddingTop: '10px', fontSize: '14px' },
}));

const ZonesAutomaticUpdate = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const selectedBrand = useSelector(state => state.Auth.selectedBrand);

  const [errors, setErrors] = useState({
    price: false,
    vat: false,
    cost: false,
    priority: false,
  });

  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false);
  const [isSynchronizationStatus, setIsSynchronizationStatus] = useState(false);
  const [lastSyncedZone, setLastSyncedZone] = useState(null);
  const [zoneCategories, setZoneCategories] = useState([]);
  const hasZoneCategories = zoneCategories.length > 0;
  const zoneCategoryOptions = hasZoneCategories
    ? zoneCategories.map(({ name, id }) => ({
        name,
        value: id,
      }))
    : [
        {
          name: t('zonesAutomaticUpdate.select.noCategories'),
          value: '',
        },
      ];

  const [zoneParamsModule, setZoneParamsModule] = useState({
    price: null,
    vat: null,
    cost: null,
    priority: null,
    autoAddToGoodspeedIntegration: false,
    zoneCategory: null,
  });

  const [isOpenModal, setIsOpenModal] = useState(false);

  const handleUpdate = e => {
    const { name, value } = e.target;

    const isValid =
      value === '' || /^\d+$/.test(value) || /^\d+[.,]\d*$/.test(value);

    if (!isValid) {
      setZoneParamsModule(prevState => ({
        ...prevState,
        [name]: '',
      }));
      return;
    }

    setZoneParamsModule(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const getErrors = () => {
    const { price, vat, cost, priority } = zoneParamsModule;
    const validationPrice = price === '' || price === null || Number(price) < 0;
    const validationVat = vat === '' || vat === null || Number(vat) < 0;
    const validationCost = cost === '' || cost === null || Number(cost) < 0;
    const validationPriority =
      priority === '' || priority === null || Number(priority) < 0;

    setErrors(prev => ({
      ...prev,
      price: validationPrice,
      vat: validationVat,
      cost: validationCost,
      priority: validationPriority,
    }));

    return {
      validationData:
        !validationPrice &&
        !validationVat &&
        !validationCost &&
        !validationPriority,
      validationValue:
        Number(price) < 0 ||
        Number(vat) < 0 ||
        Number(cost) < 0 ||
        Number(priority) < 0,
    };
  };

  const handleSwitch = e => {
    const { checked } = e.target;
    const validate = getErrors();

    if (!checked) {
      setAutoSyncEnabled(false);

      return;
    }

    if (validate.validationData) {
      setAutoSyncEnabled(true);

      return;
    }

    if (validate.validationValue) {
      toast.error(t('zonesAutomaticUpdate.toastValueError'));

      return;
    }

    toast.error(t('zonesAutomaticUpdate.toastRequiredFields'));
  };

  const handleCheckboxChange = e => {
    const { name } = e.target;
    setZoneParamsModule(prevState => ({
      ...prevState,
      [name]: !prevState[name],
    }));
  };

  const fetchModulesBrandData = async () => {
    const brandData = await get(`brands/${selectedBrand}`);

    const modules = await get(`brands/${selectedBrand}/modules`, null, {
      params: {
        module: ['ZoneDefaultParams'],
      },
    });

    try {
      const data = modules.configuration.ZoneDefaultParams;

      if (data) {
        setZoneParamsModule(prev => ({
          ...prev,
          price: data.price,
          vat: data.vat,
          cost: data.cost,
          priority: data.priority,
          autoAddToGoodspeedIntegration: data.autoAddToGoodspeedIntegration,
          zoneCategory: data.zoneCategory || null,
        }));
        setAutoSyncEnabled(brandData.autoSyncEnabled);
        setIsSynchronizationStatus(brandData.autoSyncEnabled);
      }
    } catch (error) {
      console.error('ZoneDefaultParams module not found', error);
    }
  };

  const handleSubmit = async () => {
    try {
      await put(`brands/${selectedBrand}/modules`, {
        configuration: {
          ZoneDefaultParams: {
            price: parseFloat(zoneParamsModule.price),
            vat: parseFloat(zoneParamsModule.vat),
            cost: parseFloat(zoneParamsModule.cost),
            priority: parseFloat(zoneParamsModule.priority),
            autoAddToGoodspeedIntegration:
              zoneParamsModule.autoAddToGoodspeedIntegration,
            zoneCategory: zoneParamsModule.zoneCategory || null,
          },
        },
      });

      await put(`brands/${selectedBrand}`, {
        autoSyncEnabled,
      });

      setIsSynchronizationStatus(autoSyncEnabled);

      if (isOpenModal) {
        setIsOpenModal(false);
      }

      toast.success(
        autoSyncEnabled
          ? t('zonesAutomaticUpdate.toastEnabled')
          : t('common.shared.saveSuccess')
      );
    } catch {
      toast.error(t('common.shared.saveError'));
    }
  };

  const fetchLastSyncedZone = async () => {
    try {
      const response = await get('/zones', null, {
        params: {
          partial: true,
          itemsPerPage: 1,
          page: 1,
          order: { lastSyncedAt: 'DESC' },
          properties: ['id', 'lastSyncedAt'],
          exists: { lastSyncedAt: true },
        },
      });
      const [lastUpdatedZone] = response['hydra:member'];

      if (lastUpdatedZone?.lastSyncedAt) {
        setLastSyncedZone(lastUpdatedZone.lastSyncedAt);
      }
    } catch (error) {
      console.error('Failed to fetch zones', error);
    }
  };

  const fetchZoneCategories = async () => {
    try {
      const response = await get('/zone-categories');
      const categories = response['hydra:member'];
      setZoneCategories(categories);
      if (!categories.length) {
        setZoneParamsModule(prev => ({
          ...prev,
          zoneCategory: null,
        }));
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchModulesBrandData();
    fetchLastSyncedZone();
    fetchZoneCategories();
  }, []);

  return (
    <>
      <CardWrapper>
        <CardBody className={classes.cardBody}>
          <div className={classes.titleWrapper}>
            <p className={classes.titleParagraph}>
              {t('zonesAutomaticUpdate.title')}{' '}
              <InfoWithTooltip
                title={t('zonesAutomaticUpdate.infoSyncDesc')}
                link={EXTERNAL_URLS.zonesAutomaticImport}
              />
            </p>
            <p className={classes.zoneInfoUpdateWrapper}>
              {t('zonesAutomaticUpdate.status')}{' '}
              <span
                className={classNames(
                  classes.statusTile,
                  isSynchronizationStatus
                    ? classes.statusEnabled
                    : classes.statusDisabled
                )}
              >
                {isSynchronizationStatus
                  ? t('common.shared.enabled')
                  : t('common.shared.disabled')}
              </span>
              {lastSyncedZone && isSynchronizationStatus && (
                <div className={classes.lastUpdateWrapper}>
                  <span className={classes.lastUpdate}>
                    {t('zonesAutomaticUpdate.lastUpdateLabel')}
                  </span>
                  <span className={classes.lastUpdateDate}>
                    {moment(lastSyncedZone).format('DD.MM.YYYY (HH:mm)') || ''}
                  </span>
                  <InfoIconTooltip
                    title={t('zonesAutomaticUpdate.infoUpdateDesc')}
                  />
                </div>
              )}
            </p>
          </div>

          <ul className={classes.ul}>
            <Trans
              i18nKey="zonesAutomaticUpdate.list"
              components={{ li: <li />, b: <b className={classes.bold} /> }}
            />
          </ul>

          <p className={classes.titleParagraph}>
            {t('zonesAutomaticUpdate.defaultsTitle')}
          </p>
          <p>
            <Trans
              i18nKey="zonesAutomaticUpdate.defaultsDesc"
              components={{ b: <b className={classes.bold} /> }}
            />
          </p>
          <div className={classes.formInputWrapper}>
            <FormTextInput
              type="number"
              error={errors.price}
              label={`${t('zonesAutomaticUpdate.label.clientPrice')} *`}
              name="price"
              value={zoneParamsModule.price}
              classes={classes}
              maxLength={64}
              handleChange={handleUpdate}
            />
            <FormTextInput
              error={errors.vat}
              type="number"
              label={`${t('zonesAutomaticUpdate.label.vat')} *`}
              name="vat"
              value={zoneParamsModule.vat}
              classes={classes}
              maxLength={64}
              handleChange={handleUpdate}
            />
            <FormTextInput
              error={errors.cost}
              type="number"
              label={`${t('zonesAutomaticUpdate.label.cateringCost')} *`}
              name="cost"
              value={zoneParamsModule.cost}
              classes={classes}
              maxLength={64}
              handleChange={handleUpdate}
            />
            <FormTextInput
              error={errors.priority}
              type="number"
              label={
                <>
                  {t('zonesAutomaticUpdate.label.priority')} *{' '}
                  <InfoIconTooltip
                    title={t('zonesAutomaticUpdate.infoPriorityDesc')}
                  />
                </>
              }
              name="priority"
              value={zoneParamsModule.priority}
              classes={classes}
              maxLength={64}
              handleChange={handleUpdate}
            />
            <div className={classes.selectWrapper}>
              <FormLabel
                className={classNames(classes.labelHorizontal, classes.label)}
              >
                {t('zonesAutomaticUpdate.categories')}
              </FormLabel>
              <SelectInput
                noGrid={true}
                classes={classes}
                name="zoneCategory"
                mapBy="name"
                trackBy="value"
                options={zoneCategoryOptions}
                value={hasZoneCategories ? zoneParamsModule.zoneCategory : null}
                disabledMenuItems={
                  hasZoneCategories ? undefined : [{ value: '' }]
                }
                handleChange={handleUpdate}
              />
            </div>
          </div>
          <div className={classes.checkboxWrapper}>
            <RegularCheckbox
              customClasses={{ root: classes.checkbox }}
              name="autoAddToGoodspeedIntegration"
              checked={zoneParamsModule.autoAddToGoodspeedIntegration}
              onClick={handleCheckboxChange}
            />
            <span>{t('zonesAutomaticUpdate.checkbox.addToIntegration')}</span>
            <InfoWithTooltip
              title={t('zonesAutomaticUpdate.infoIntegrationDesc')}
              link={EXTERNAL_URLS.zonesGoodspeedIntegration}
            />
          </div>
          <div className={classes.switchWrapper}>
            <Switch
              color="default"
              className={classes.switch}
              classes={{
                switchBase: classes.customSwitchBase,
                checked: classes.customChecked,
                bar: classes.customBar,
              }}
              checked={autoSyncEnabled}
              onChange={handleSwitch}
            />
            <span>{t('zonesAutomaticUpdate.switch.allowAutoUpdate')}</span>
          </div>
        </CardBody>
      </CardWrapper>
      <FormControlButtons
        classes={classes}
        submitText={t('common.shared.save')}
        handleSubmit={() => {
          if (isSynchronizationStatus && !autoSyncEnabled) {
            setIsOpenModal(true);

            return;
          }

          handleSubmit();
        }}
      />
      <Dialog fullWidth={true} open={isOpenModal}>
        <SweetAlert
          warning={true}
          title={t('zonesAutomaticUpdate.confirmDisableMessage')}
          onConfirm={handleSubmit}
          onCancel={() => setIsOpenModal(false)}
          confirmBtnCssClass={classNames(classes.button, classes.danger)}
          cancelBtnCssClass={classes.button}
          confirmBtnText={t('zonesAutomaticUpdate.confirmDisable')}
          cancelBtnText={t('common.cancel')}
          showCancel={true}
        ></SweetAlert>
      </Dialog>
    </>
  );
};

export default ZonesAutomaticUpdate;
